import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

const EASE = [0.22, 1, 0.36, 1];

export const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: EASE },
};

export const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.18 }}
    transition={{ duration: 0.65, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

// Re-triggering reveal: animates in when scrolling into view AND out when leaving view.
// Use this for Home sections to get a smooth entry/exit cinematic feel.
export const RevealInOut = ({
  children,
  delay = 0,
  className = '',
  y = 40,
  amount = 0.15,
}) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: false, amount }}
    transition={{ duration: 0.75, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

// Cursor-driven parallax for an inner element. Wrap the image/content with this.
// strength: max translation in pixels (default 22)
export const ParallaxMouse = ({ children, strength = 22, className = '' }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Disable on touch devices (no mouse cursor)
    if (window.matchMedia('(hover: none), (pointer: coarse)').matches) return;

    let raf = 0;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      // Center of element relative to viewport
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      // Normalize -1 .. 1 based on viewport (smooth far-from-element response)
      const nx = (e.clientX - cx) / (window.innerWidth / 2);
      const ny = (e.clientY - cy) / (window.innerHeight / 2);
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setPos({ x: Math.max(-1, Math.min(1, nx)), y: Math.max(-1, Math.min(1, ny)) });
      });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div ref={ref} className={className} style={{ perspective: '1000px' }}>
      <motion.div
        animate={{
          x: pos.x * strength,
          y: pos.y * strength,
          rotateY: pos.x * 3,
          rotateX: -pos.y * 3,
        }}
        transition={{ type: 'spring', stiffness: 60, damping: 18, mass: 0.6 }}
        style={{ transformStyle: 'preserve-3d', willChange: 'transform' }}
      >
        {children}
      </motion.div>
    </div>
  );
};

export const PageTransition = ({ children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.35, ease: EASE }}
  >
    {children}
  </motion.div>
);
